import { useEffect, useRef, useState, useCallback } from "react";
import api, { apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { LogOut, Save, Plus, Trash2, Upload, Check, FileDown, ShieldQuestion, Lock, Camera } from "lucide-react";
import { toast } from "sonner";
import { DatePicker } from "@/components/pickers";

const DECLARATION = "I hereby declare that the above statements made in my application form are true, complete and correct to the best of my knowledge and belief. In the event of any information being found false or incorrect at any stage, my services are liable to be terminated without notice.";

const DOC_FIELDS = [
  { field: "photo", label: "Photograph", section: 1 },
  { field: "aadhaar_front", label: "Aadhaar front (or full PDF)", section: 6 },
  { field: "aadhaar_back", label: "Aadhaar back", section: 6 },
  { field: "pan_card", label: "PAN card", section: 6 },
  { field: "voter_id", label: "Voter ID (optional)", section: 6 },
  { field: "bank_proof", label: "Passbook / cancelled cheque", section: 6 },
  { field: "signature", label: "Signature (on white paper)", section: 6 },
];

function F({ label, value, onChange, type = "text", placeholder, testid, readOnly }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      {type === "date" && !readOnly ? (
        <div className="mt-1"><DatePicker value={value} onChange={onChange} clearable testid={testid} /></div>
      ) : (
        <Input type={type === "date" ? "text" : type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          readOnly={readOnly} data-testid={testid} className={`mt-1 ${readOnly ? "bg-slate-50" : ""}`} />
      )}
    </div>
  );
}

function RepeatTable({ label, rows, cols, onChange, max, min = 0, testid }) {
  const add = () => onChange([...(rows || []), {}]);
  const del = (i) => onChange(rows.filter((_, x) => x !== i));
  const set = (i, k, v) => onChange(rows.map((r, x) => (x === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2" data-testid={testid}>
      {(rows || []).map((r, i) => (
        <div key={i} className="grid grid-cols-2 gap-2 rounded-lg border border-slate-100 bg-slate-50/50 p-3 md:grid-cols-3">
          {cols.map((c) => (
            <div key={c.k}>
              <Label className="text-[11px] text-slate-500">{c.label}</Label>
              {c.k === "dob" ? (
                <div className="mt-0.5"><DatePicker value={r[c.k]} onChange={(v) => set(i, c.k, v)} clearable className="h-8 text-sm" /></div>
              ) : (
                <Input value={r[c.k] || ""} onChange={(e) => set(i, c.k, e.target.value)} placeholder={c.ph}
                  className="mt-0.5 h-8 text-sm" />
              )}
            </div>
          ))}
          {(rows.length > min) && (
            <button onClick={() => del(i)} className="col-span-full justify-self-end text-xs text-rose-500">
              <Trash2 className="inline h-3.5 w-3.5" /> Remove
            </button>
          )}
        </div>
      ))}
      {(!max || (rows || []).length < max) && (
        <Button type="button" size="sm" variant="outline" onClick={add}><Plus className="mr-1 h-3.5 w-3.5" /> Add row</Button>
      )}
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <Card className="p-5" data-testid={`section-${n}`}>
      <h2 className="mb-4 text-base font-semibold text-indigo-900">{n}. {title}</h2>
      <div className="space-y-3">{children}</div>
    </Card>
  );
}

export default function JoiningForm() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [data, setData] = useState({});
  const [docs, setDocs] = useState([]);
  const [savedAt, setSavedAt] = useState(null);
  const [review, setReview] = useState(false);
  const [declName, setDeclName] = useState("");
  const [declChecked, setDeclChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [crOpen, setCrOpen] = useState(false);
  const [crSections, setCrSections] = useState("");
  const [crReason, setCrReason] = useState("");
  const saveTimer = useRef();
  const firstLoad = useRef(true);

  const load = useCallback(async () => {
    const { data: d } = await api.get("/forms/me");
    setState(d);
    setData(d.draft || {});
    setSavedAt(d.saved_at);
    const dl = await api.get("/forms/me/documents");
    setDocs(dl.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const locked = state && (state.form_status === "submitted" || state.form_status === "change_requested");

  // autosave debounced 1.5s
  useEffect(() => {
    if (!state || firstLoad.current) { firstLoad.current = false; return; }
    if (locked) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const { data: r } = await api.put("/forms/me/draft", { data });
        setSavedAt(r.saved_at);
      } catch (e) {}
    }, 1500);
    return () => clearTimeout(saveTimer.current);
  }, [data, state, locked]);

  const sec = (s) => data[s] || {};
  const setSec = (s, patch) => setData((d) => ({ ...d, [s]: { ...(d[s] || {}), ...patch } }));
  const f1 = (k) => (v) => setSec("section1", { [k]: v });

  const chooseVariant = async (mode) => {
    await api.post("/forms/me/variant", { mode });
    toast.success("Saved");
    load();
  };
  const giveConsent = async () => {
    await api.post("/forms/me/consent");
    load();
  };

  const uploadDoc = async (field, file) => {
    const fd = new FormData();
    fd.append("field", field);
    fd.append("file", file);
    try {
      await api.post("/forms/me/documents", fd);
      toast.success("Uploaded");
      const dl = await api.get("/forms/me/documents");
      setDocs(dl.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const hasDoc = (field) => docs.some((d) => d.field === field);

  const doSubmit = async () => {
    setSubmitting(true);
    const locReq = state.org.location_required_on_submit;
    const finish = async (location) => {
      try {
        const { data: r } = await api.post("/forms/me/submit", {
          data, declaration_full_name: declName, declaration_checked: declChecked,
          place: sec("section6").place || "", location,
        });
        toast.success(`Submitted (v${r.version})`);
        setReview(false);
        load();
      } catch (e) {
        const det = e.response?.data?.detail;
        if (det?.errors) toast.error(det.errors.map((x) => x.message).join("; "));
        else toast.error(apiError(det));
      } finally { setSubmitting(false); }
    };
    if (!navigator.geolocation) {
      if (locReq) { toast.error("Location not available on this device"); setSubmitting(false); return; }
      return finish(null);
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => finish({ lat: pos.coords.latitude, long: pos.coords.longitude,
        accuracy: Math.round(pos.coords.accuracy), device_timestamp: new Date().toISOString() }),
      (err) => {
        if (locReq) { toast.error("Location is required. Please allow location access and try again."); setSubmitting(false); }
        else finish(null);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const raiseCR = async () => {
    try {
      await api.post("/forms/me/change-request", {
        sections: crSections.split(",").map((s) => s.trim()).filter(Boolean), reason: crReason });
      toast.success("Change request sent");
      setCrOpen(false); setCrSections(""); setCrReason("");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const downloadPdf = async () => {
    const res = await api.get("/forms/me/pdf", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "joining_form.pdf"; a.click();
    URL.revokeObjectURL(url);
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-slate-50 pb-28">
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
        <div><span className="font-bold text-slate-900">Infiblu</span><span className="ml-2 text-xs text-slate-400">Joining Form</span></div>
        <div className="flex items-center gap-3">
          {savedAt && !locked && <span className="text-xs text-slate-400" data-testid="saved-indicator">Saved {new Date(savedAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" })}</span>}
          <Button variant="outline" size="sm" data-testid="employee-logout" onClick={() => { logout(); navigate("/login"); }}><LogOut className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="mx-auto max-w-2xl px-4 py-6">{children}</div>
    </div>
  );

  if (!state) return <Shell><p className="text-sm text-slate-500">Loading…</p></Shell>;

  // 1. Variant choice
  if (state.needs_variant_choice) {
    return (
      <Shell>
        <Card className="p-6" data-testid="variant-chooser">
          <div className="mb-3 flex items-center gap-2 text-indigo-700"><ShieldQuestion className="h-5 w-5" /><h2 className="font-semibold">Statutory coverage</h2></div>
          <p className="mb-4 text-sm text-slate-600">Will you be covered under PF / ESI?</p>
          <div className="space-y-3">
            <Button className="w-full" data-testid="variant-with" onClick={() => chooseVariant("with_statutory")}>I will be covered under PF/ESI</Button>
            <Button className="w-full" variant="outline" data-testid="variant-without" onClick={() => chooseVariant("without_statutory")}>I will not be covered</Button>
          </div>
          <p className="mt-3 text-xs text-slate-400">If unsure, ask your HR.</p>
        </Card>
      </Shell>
    );
  }

  // 2. Consent
  if (!state.consent_at && !locked) {
    return (
      <Shell>
        <Card className="p-6" data-testid="consent-notice">
          <h2 className="mb-2 font-semibold text-indigo-900">Before you begin</h2>
          <p className="text-sm text-slate-600">We collect your personal, bank and statutory details to create and maintain your employment and payroll records with {state.org.name}, and to file statutory returns (PF/ESI) where applicable.</p>
          <p className="mt-2 text-sm text-slate-600">Your data is visible to your organisation's admins and Infiblu staff supporting them. Sensitive fields (Aadhaar, PAN, bank account) are encrypted and shown masked on screen.</p>
          <Button className="mt-5 w-full" data-testid="consent-accept" onClick={giveConsent}>I understand and consent</Button>
        </Card>
      </Shell>
    );
  }

  const variantB = state.variant === "with_statutory";

  // Locked (submitted / change requested)
  if (locked) {
    const latestCr = state.change_requests?.[0];
    return (
      <Shell>
        <Card className="mb-4 flex items-center gap-3 p-5" data-testid="locked-banner">
          <Lock className="h-5 w-5 text-emerald-600" />
          <div>
            <p className="font-medium text-slate-800">Form submitted (version {state.current_version})</p>
            <p className="text-xs text-slate-500">Your form is locked. You can download a copy or request a change.</p>
          </div>
        </Card>
        {latestCr && (
          <Card className="mb-4 p-4" data-testid="cr-status">
            <p className="text-sm"><b>Change request:</b> {latestCr.status}</p>
            <p className="text-xs text-slate-500">Sections: {(latestCr.sections || []).join(", ")} · {latestCr.reason}</p>
            {latestCr.comment && <p className="mt-1 text-xs text-slate-600">Admin comment: {latestCr.comment}</p>}
          </Card>
        )}
        <div className="flex gap-2">
          <Button onClick={downloadPdf} data-testid="download-pdf"><FileDown className="mr-2 h-4 w-4" /> Download PDF</Button>
          <Button variant="outline" onClick={() => setCrOpen(true)} data-testid="raise-cr">Request a change</Button>
        </div>
        <Dialog open={crOpen} onOpenChange={setCrOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Request a change</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <F label="Which sections? (comma separated, e.g. 1, 6)" value={crSections} onChange={setCrSections} testid="cr-sections" />
              <div><Label className="text-xs">Reason</Label><Textarea value={crReason} onChange={(e) => setCrReason(e.target.value)} className="mt-1" data-testid="cr-reason" /></div>
            </div>
            <DialogFooter><Button onClick={raiseCR} disabled={!crReason} data-testid="cr-submit">Send request</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </Shell>
    );
  }

  // Editable form (draft / reopened)
  const s1 = sec("section1"), s6 = sec("section6"), s7 = sec("section7"), s8 = sec("section8"), s9 = sec("section9");
  return (
    <Shell>
      {state.form_status === "reopened" && (
        <Card className="mb-4 bg-blue-50 p-3 text-sm text-blue-800" data-testid="reopened-banner">Your change request was approved. Edit and resubmit.</Card>
      )}
      <div className="space-y-4">
        <Section n={1} title="Personal Details">
          <div className="grid gap-3 md:grid-cols-2">
            <F label="Full name" value={s1.full_name} onChange={f1("full_name")} testid="s1-full-name" />
            <F label="Father's name" value={s1.fathers_name} onChange={f1("fathers_name")} testid="s1-fathers-name" />
          </div>
          <p className="pt-2 text-xs font-medium text-slate-500">Correspondence address</p>
          <div className="grid gap-3 md:grid-cols-2">
            <F label="Line 1" value={s1.corr_line1} onChange={f1("corr_line1")} />
            <F label="Line 2" value={s1.corr_line2} onChange={f1("corr_line2")} />
            <F label="City" value={s1.corr_city} onChange={f1("corr_city")} />
            <F label="District" value={s1.corr_district} onChange={f1("corr_district")} />
            <F label="State" value={s1.corr_state} onChange={f1("corr_state")} />
            <F label="PIN" value={s1.corr_pin} onChange={f1("corr_pin")} testid="s1-corr-pin" />
          </div>
          <label className="flex items-center gap-2 pt-2 text-sm text-slate-600">
            <Checkbox checked={!!s1.same_as_corr} data-testid="same-as-corr"
              onCheckedChange={(v) => setSec("section1", { same_as_corr: v, ...(v ? {
                perm_line1: s1.corr_line1, perm_line2: s1.corr_line2, perm_city: s1.corr_city,
                perm_district: s1.corr_district, perm_state: s1.corr_state, perm_pin: s1.corr_pin } : {}) })} />
            Permanent address same as correspondence
          </label>
          {!s1.same_as_corr && (
            <div className="grid gap-3 md:grid-cols-2">
              <F label="Perm. Line 1" value={s1.perm_line1} onChange={f1("perm_line1")} />
              <F label="Perm. Line 2" value={s1.perm_line2} onChange={f1("perm_line2")} />
              <F label="Perm. City" value={s1.perm_city} onChange={f1("perm_city")} />
              <F label="Perm. District" value={s1.perm_district} onChange={f1("perm_district")} />
              <F label="Perm. State" value={s1.perm_state} onChange={f1("perm_state")} />
              <F label="Perm. PIN" value={s1.perm_pin} onChange={f1("perm_pin")} />
            </div>
          )}
          <div className="grid gap-3 md:grid-cols-2">
            <F label="Telephone (optional)" value={s1.telephone} onChange={f1("telephone")} />
            <F label="Mobile" value={s1.mobile} onChange={f1("mobile")} testid="s1-mobile" />
            <F label="Personal email" value={state.employee.email} onChange={() => {}} readOnly testid="s1-email" />
            <F label="Date of birth" type="date" value={s1.date_of_birth} onChange={f1("date_of_birth")} testid="s1-dob" />
            <div>
              <Label className="text-xs text-slate-600">Gender</Label>
              <Select value={s1.gender || ""} onValueChange={f1("gender")}>
                <SelectTrigger className="mt-1" data-testid="s1-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{["Male", "Female", "Other"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Marital status</Label>
              <Select value={s1.marital_status || ""} onValueChange={f1("marital_status")}>
                <SelectTrigger className="mt-1" data-testid="s1-marital"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{["Unmarried", "Married", "Other"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <F label="PAN" value={s1.pan} onChange={(v) => f1("pan")(v.toUpperCase())} placeholder="ABCDE1234F" testid="s1-pan" />
            <F label="Blood group" value={s1.blood_group} onChange={f1("blood_group")} />
          </div>
          <p className="pt-2 text-xs font-medium text-slate-500">Emergency contact</p>
          <div className="grid gap-3 md:grid-cols-3">
            <F label="Name" value={s1.emergency_name} onChange={f1("emergency_name")} />
            <F label="Relation" value={s1.emergency_relation} onChange={f1("emergency_relation")} />
            <F label="Contact number" value={s1.emergency_number} onChange={f1("emergency_number")} />
          </div>
          <FileField field="photo" label="Photograph" has={hasDoc("photo")} onUpload={uploadDoc} />
        </Section>

        <Section n={2} title="Educational Details">
          <RepeatTable testid="s2-table" rows={sec("section2").rows} min={1} max={5}
            cols={[{ k: "degree", label: "Degree" }, { k: "institute", label: "University/Institute" },
              { k: "from", label: "From (MM-YYYY)" }, { k: "to", label: "To (MM-YYYY)" },
              { k: "percentage", label: "%/Grade" }, { k: "specialisation", label: "Specialisation" }]}
            onChange={(rows) => setSec("section2", { rows })} />
        </Section>

        <Section n={3} title="Employment Details (last 3)">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <Checkbox checked={!!sec("section3").fresher} data-testid="s3-fresher"
              onCheckedChange={(v) => setSec("section3", { fresher: v })} /> I am a fresher
          </label>
          {!sec("section3").fresher && (
            <RepeatTable testid="s3-table" rows={sec("section3").rows} max={3}
              cols={[{ k: "organisation", label: "Organisation" }, { k: "designation", label: "Designation" },
                { k: "from", label: "From" }, { k: "to", label: "To" }, { k: "ctc", label: "Annual CTC" }]}
              onChange={(rows) => setSec("section3", { rows })} />
          )}
        </Section>

        <Section n={4} title="Family Details">
          <RepeatTable testid="s4-table" rows={sec("section4").rows} max={5}
            cols={[{ k: "name", label: "Name" }, { k: "relation", label: "Relation" },
              { k: "occupation", label: "Occupation" }, { k: "dob", label: "Date of birth" }]}
            onChange={(rows) => setSec("section4", { rows })} />
        </Section>

        <Section n={5} title="Professional References (2)">
          <RepeatTable testid="s5-table" rows={sec("section5").rows} min={0} max={2}
            cols={[{ k: "name", label: "Name" }, { k: "organisation", label: "Organisation" },
              { k: "designation", label: "Designation" }, { k: "contact_number", label: "Contact number" }]}
            onChange={(rows) => setSec("section5", { rows })} />
        </Section>

        <Section n={6} title="Bank, Documents & Declaration">
          <div className="grid gap-3 md:grid-cols-2">
            <F label="Account holder name" value={s6.account_holder} onChange={(v) => setSec("section6", { account_holder: v })} />
            <F label="Bank name" value={s6.bank_name} onChange={(v) => setSec("section6", { bank_name: v })} />
            <F label="Branch" value={s6.branch} onChange={(v) => setSec("section6", { branch: v })} />
            <F label="Account type" value={s6.account_type} onChange={(v) => setSec("section6", { account_type: v })} />
            <F label="Account number" value={s6.account_number} onChange={(v) => setSec("section6", { account_number: v })} testid="s6-account" />
            <F label="Re-enter account number" value={s6.account_number_confirm} onChange={(v) => setSec("section6", { account_number_confirm: v })} testid="s6-account-confirm" />
            <F label="IFSC" value={s6.ifsc} onChange={(v) => setSec("section6", { ifsc: v.toUpperCase() })} placeholder="SBIN0001234" testid="s6-ifsc" />
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            {DOC_FIELDS.filter((d) => d.section === 6).map((d) => (
              <FileField key={d.field} field={d.field} label={d.label} has={hasDoc(d.field)} onUpload={uploadDoc} />
            ))}
          </div>
          <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">{DECLARATION}</div>
          <F label="Place" value={s6.place} onChange={(v) => setSec("section6", { place: v })} testid="s6-place" />
        </Section>

        {variantB && (
          <>
            <Section n={7} title="Particulars for ESI / PF">
              <div className="grid gap-3 md:grid-cols-2">
                <F label="Name (block letters)" value={s7.name_block} onChange={(v) => setSec("section7", { name_block: v.toUpperCase() })} />
                <F label="Father/Husband name" value={s7.father_husband_name} onChange={(v) => setSec("section7", { father_husband_name: v })} />
                <F label="Aadhaar number" value={s7.aadhaar_number} onChange={(v) => setSec("section7", { aadhaar_number: v })} testid="s7-aadhaar" />
                <F label="Mobile linked with Aadhaar" value={s7.mobile_linked_aadhaar} onChange={(v) => setSec("section7", { mobile_linked_aadhaar: v })} />
                <F label="Previous UAN / PF (optional)" value={s7.previous_uan} onChange={(v) => setSec("section7", { previous_uan: v })} />
                <F label="Previous ESIC IP (optional)" value={s7.previous_esic_ip} onChange={(v) => setSec("section7", { previous_esic_ip: v })} />
                <F label="ESIC dispensary name (optional)" value={s7.esic_dispensary_name} onChange={(v) => setSec("section7", { esic_dispensary_name: v })} />
                <F label="ESIC dispensary code (optional)" value={s7.esic_dispensary_code} onChange={(v) => setSec("section7", { esic_dispensary_code: v })} />
              </div>
              <p className="pt-2 text-xs font-medium text-slate-500">Family particulars</p>
              <RepeatTable testid="s7-family" rows={s7.family} onChange={(family) => setSec("section7", { family })}
                cols={[{ k: "name", label: "Name" }, { k: "dob", label: "DOB" }, { k: "relationship", label: "Relationship" },
                  { k: "aadhaar", label: "Aadhaar (optional)" }, { k: "residing", label: "Residing with you (Y/N)" }]} />
              <p className="pt-2 text-xs font-medium text-slate-500">ESI nominee</p>
              <div className="grid gap-3 md:grid-cols-3">
                <F label="Name" value={s7.esi_nominee_name} onChange={(v) => setSec("section7", { esi_nominee_name: v })} />
                <F label="DOB" type="date" value={s7.esi_nominee_dob} onChange={(v) => setSec("section7", { esi_nominee_dob: v })} />
                <F label="Relationship" value={s7.esi_nominee_relationship} onChange={(v) => setSec("section7", { esi_nominee_relationship: v })} />
              </div>
            </Section>

            <Section n={8} title="Form 2 Part A — EPF Nomination">
              <RepeatTable testid="s8-nominees" rows={s8.nominees} onChange={(nominees) => setSec("section8", { nominees })}
                cols={[{ k: "name", label: "Nominee name" }, { k: "address", label: "Address" },
                  { k: "relationship", label: "Relationship" }, { k: "dob", label: "DOB" },
                  { k: "share", label: "Share %" }, { k: "guardian_name", label: "Guardian (if minor)" }]} />
              <p className="text-xs text-slate-400">Shares must total exactly 100%.</p>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Checkbox checked={!!s8.no_family} onCheckedChange={(v) => setSec("section8", { no_family: v })} />
                I have no family as defined in para 2(g) of the EPF Scheme 1952
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Checkbox checked={!!s8.parents_dependent} onCheckedChange={(v) => setSec("section8", { parents_dependent: v })} />
                My father/mother is/are dependent upon me
              </label>
            </Section>

            <Section n={9} title="Form 2 Part B — EPS">
              <RepeatTable testid="s9-family" rows={s9.family} onChange={(family) => setSec("section9", { family })}
                cols={[{ k: "name_address", label: "Name & address" }, { k: "age", label: "Age" }, { k: "relationship", label: "Relationship" }]} />
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Checkbox checked={!!s9.no_family} onCheckedChange={(v) => setSec("section9", { no_family: v })} />
                I have no family as defined in para 2(vii) of the EPS 1995
              </label>
              {s9.no_family && (
                <div className="grid gap-3 md:grid-cols-3">
                  <F label="Widow nominee name & address" value={s9.widow_nominee_name_address} onChange={(v) => setSec("section9", { widow_nominee_name_address: v })} />
                  <F label="DOB" type="date" value={s9.widow_nominee_dob} onChange={(v) => setSec("section9", { widow_nominee_dob: v })} />
                  <F label="Relationship" value={s9.widow_nominee_relationship} onChange={(v) => setSec("section9", { widow_nominee_relationship: v })} />
                </div>
              )}
            </Section>
          </>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 border-t border-slate-200 bg-white px-4 py-3">
        <div className="mx-auto flex max-w-2xl items-center justify-between">
          <span className="text-xs text-slate-400">{savedAt ? "Draft autosaved" : "Not saved yet"}</span>
          <Button onClick={() => { setDeclName(""); setDeclChecked(false); setReview(true); }} data-testid="review-submit-btn">Review & submit</Button>
        </div>
      </div>

      <Dialog open={review} onOpenChange={setReview}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Review & submit</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Please confirm your details are correct. On submit we capture your location and a server timestamp.</p>
          <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">{DECLARATION}</div>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <Checkbox checked={declChecked} onCheckedChange={setDeclChecked} data-testid="decl-check" className="mt-0.5" />
            I accept the declaration above
          </label>
          <F label="Type your full name to confirm" value={declName} onChange={setDeclName} testid="decl-name" />
          {state.org.location_required_on_submit && <p className="text-xs text-amber-600">Location access is required to submit. Please allow it when prompted.</p>}
          <DialogFooter>
            <Button disabled={submitting || !declChecked || !declName} onClick={doSubmit} data-testid="final-submit">
              {submitting ? "Submitting…" : "Submit form"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

function FileField({ field, label, has, onUpload }) {
  const ref = useRef();
  const camRef = useRef();
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50/50 px-3 py-2">
      <span className="text-xs text-slate-600">{label}{has && <Check className="ml-1 inline h-3.5 w-3.5 text-emerald-600" />}</span>
      <div className="flex items-center gap-2">
        <input ref={ref} type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" data-testid={`upload-${field}`}
          onChange={(e) => { if (e.target.files?.[0]) onUpload(field, e.target.files[0]); e.target.value = ""; }} />
        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid={`camera-${field}`}
          onChange={(e) => { if (e.target.files?.[0]) onUpload(field, e.target.files[0]); e.target.value = ""; }} />
        <Button type="button" size="sm" variant="outline" className="h-7 px-2" title="Take a photo"
          data-testid={`camera-btn-${field}`} onClick={() => camRef.current?.click()}>
          <Camera className="h-3.5 w-3.5" />
        </Button>
        <Button type="button" size="sm" variant="outline" className="h-7" onClick={() => ref.current?.click()}>
          <Upload className="mr-1 h-3 w-3" /> {has ? "Replace" : "Upload"}
        </Button>
      </div>
    </div>
  );
}
